import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchEntries = createAsyncThunk(
  'bookkeeping/fetchEntries',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit !== undefined) {
        queryParams.append('limit', params.limit);
      }
      if (params.skip !== undefined) {
        queryParams.append('skip', params.skip);
      }
      if (params.chartOfAccount !== undefined && params.chartOfAccount !== '') {
        queryParams.append('chartOfAccount', params.chartOfAccount);
      }
      const queryString = queryParams.toString();
      const url = queryString ? `/bookkeeping?${queryString}` : '/bookkeeping';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addEntry = createAsyncThunk(
  'bookkeeping/addEntry',
  async (entryData, { rejectWithValue }) => {
    try {
      const response = await api.post('/bookkeeping', entryData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntry = createAsyncThunk(
  'bookkeeping/updateEntry',
  async ({ id, entryData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/bookkeeping/${id}`, entryData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteEntry = createAsyncThunk(
  'bookkeeping/deleteEntry',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/bookkeeping/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const bookkeepingSlice = createSlice({
  name: 'bookkeeping',
  initialState: {
    entries: [],
    totalCount: 0,
    loading: false,
    error: null
  },
  reducers: {
    clearBookkeepingState: () => ({
      entries: [],
      totalCount: 0,
      loading: false,
      error: null
    })
  },
  extraReducers: (builder) => {
    builder
      // Fetch entries
      .addCase(fetchEntries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEntries.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both old format (array) and new format (object with entries and totalCount)
        if (Array.isArray(action.payload)) {
          state.entries = action.payload;
          state.totalCount = action.payload.length;
        } else if (action.payload && action.payload.entries) {
          state.entries = action.payload.entries;
          state.totalCount = action.payload.totalCount || 0;
        } else {
          state.entries = [];
          state.totalCount = 0;
        }
        state.error = null;
      })
      .addCase(fetchEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add entry
      .addCase(addEntry.pending, (state) => {
        state.loading = true;
      })
      .addCase(addEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.entries.push(action.payload);
        state.error = null;
      })
      .addCase(addEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update entry
      .addCase(updateEntry.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateEntry.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.entries.findIndex(entry => entry._id === action.payload._id);
        if (index !== -1) {
          state.entries[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete entry
      .addCase(deleteEntry.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = state.entries.filter(entry => 
          (entry._id || entry.id) !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearBookkeepingState } = bookkeepingSlice.actions;
export default bookkeepingSlice.reducer; 